import { domains, fetchModule, composerSettings } from '@ddc-webplatform/cmsc-modules';
import { CMSC_API, LANGUAGES, SITE_ID, } from 'constants/composer';
import { canAccessFordDirectProgram } from 'constants/ui';
import { SitemapEntry } from 'models';
import { isExternalUrl } from './utils';

type SitemapExportEntry = SitemapEntry & {
	inHtmSitemap: boolean;
	inXmlSitemap: boolean;
};

const isHiddenInDefaultTree = (entry: SitemapExportEntry): boolean => {
	const path = entry.path || '';
	return isExternalUrl(path);
};

/**
 * Escape a value for safe CSV output
 * @param val - Value to escape
 * @returns CSV-safe string
 */
const escapeCSV = (val: unknown): string => {
	if (val === null) return '';
	const str = String(val);
	if (str.includes(',') || str.includes('"') || str.includes('\n')) {
		return `"${str.replace(/"/g, '""')}"`;
	}
	return str;
};

/**
 * Convert sitemap data to CSV format
 * @param sitemapEntriesByVariant - Sitemap entries grouped by variant
 * @param languages - Array of language codes
 * @param cmsDomain - CMS domain URL
 * @returns CSV formatted string
 */
const getSitemapDataAsCSV = (
	sitemapEntriesByVariant: Record<string, SitemapExportEntry[]>,
	languages: string[],
	cmsDomain: string
): string => {
	const headerRow = ['path[default]'];
	// add header for each locale
	languages.forEach(lang => headerRow.push(`path[${lang}]`));
	// add additional headers for xml/html sitemap and newline
	const csvRows = [
		headerRow.concat(['inHtmSitemap', 'inXmlSitemap']).join(',')
	];
	sitemapEntriesByVariant.CONTROL
		?.filter(sitemapEntry => !isHiddenInDefaultTree(sitemapEntry))
		.forEach(sitemapEntry => {
		const { path, pathsByLocale, inHtmSitemap, inXmlSitemap } = sitemapEntry;

		const rowContent = [path ? escapeCSV(`${cmsDomain}${path}`) : ''];

		languages.forEach(lang => {
			const pathForLocale = pathsByLocale?.[lang];
			rowContent.push(
				pathForLocale ? escapeCSV(`${cmsDomain}${pathForLocale}`) : ''
			);
		});

		rowContent.push(escapeCSV(inHtmSitemap), escapeCSV(inXmlSitemap));

		csvRows.push(rowContent.join(','));
	});

	return csvRows.join('\n');
};

const resolveCsvDomain = (siteId: string, profileId: string): string => {
	const isProfile = profileId === '' && /profile$/.test(siteId);
	const useInternalDomain =
		siteId === 'cmsctemplates' ||
		isProfile ||
		siteId.endsWith('2') ||
		canAccessFordDirectProgram;

	if (useInternalDomain) {
		return `https://${siteId}.cms.dealer.com`;
	}

	const rawDomain = domains.getCMSDomain();
	if (!rawDomain) throw new Error('CMS domain not found');
	return `https://${rawDomain}`;
};

/**
 * Export sitemap data as CSV file
 * Downloads sitemap entries and triggers CSV download
 */
export const exportSitemapAsCSV = async (): Promise<{ success: boolean }> => {
	if (!SITE_ID) {
		throw new Error('Site ID not found');
	}

	// Fetch sitemap entries using fetchWithAuth
	const response = await fetchModule.fetchWithAuth(
		`${CMSC_API}/sites/${SITE_ID}/sitemap-entries/all`
	);

	if (!response.ok) {
		throw new Error(`Failed to fetch sitemap data: ${response.statusText}`);
	}
	const result = await response.json();
	const { profileId } = composerSettings.getComposerSettings();
	const cmsDomain = resolveCsvDomain(SITE_ID, profileId);
	const csvData = getSitemapDataAsCSV(result, LANGUAGES, cmsDomain);

	// Trigger download
	const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = `sitemap-${SITE_ID}-${new Date().toISOString().split('T')[0]}.csv`;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);

	return { success: true };
};
